"use client";
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Bot, Send, User, Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { AssistantMarkdown } from "@/components/ai/assistant-markdown";

type Message = { id: string; role: "user" | "assistant"; content: string };

const SEED_MESSAGE: Message = {
  id: "seed",
  role: "assistant",
  content:
    "I'm the DRR AI Assistant — a restoration industry expert trained on IICRC standards, NESHAP, AZDEQ, ICRA 2.0, and every phase of the mitigation and restoration process. Tell me about your situation and I'll help you figure out exactly what needs to happen.",
};

export default function AiTriagePage() {
  const [messages, setMessages] = useState<Message[]>([SEED_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    setError(null);

    const userMsg: Message = { id: `u-${Date.now()}`, role: "user", content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    // Reset textarea height
    if (inputRef.current) inputRef.current.style.height = "44px";
    setLoading(true);

    const msgId = `a-${Date.now()}`;
    let fullText = "";

    try {
      const res = await fetch("/api/triage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next
            .filter((m) => m.id !== "seed")
            .map(({ role, content }) => ({ role, content })),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          data?.error ?? data?.message ?? "The assistant couldn't process that request. Try again or call " + BRAND.phone,
        );
        return;
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") break;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) {
              setError(`The AI Assistant is temporarily unavailable. Call ${BRAND.phone}.`);
            } else if (typeof parsed.text === "string") {
              if (fullText === "") {
                // First token — add the placeholder message and stop the spinner
                setMessages((prev) => [
                  ...prev,
                  { id: msgId, role: "assistant", content: "" },
                ]);
                setLoading(false);
              }
              fullText += parsed.text;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, content: fullText } : m,
                ),
              );
            }
          } catch {
            // ignore malformed SSE chunks
          }
        }
      }

      // If stream ended but we never got content, show an error
      if (fullText === "") {
        setError("No response received. Please try again or call " + BRAND.phone + ".");
      }
    } catch {
      setError("Network error. For immediate response call " + BRAND.phone + ".");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 130) + "px";
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage:
          "url('/images/arizona-landmarks/sedona-red-rocks-arizona.jpg')",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
      }}
    >
      {/* Page overlay */}
      <div style={{ background: "rgba(5,13,24,0.60)", minHeight: "100vh" }}>
        <div
          style={{
            maxWidth: "860px",
            margin: "0 auto",
            padding: "96px 20px 60px",
          }}
        >
          {/* ── Header ── */}
          <div style={{ marginBottom: "24px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(220,38,38,0.14)",
                border: "1px solid rgba(220,38,38,0.30)",
                borderRadius: "100px",
                padding: "5px 14px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "#D4A84B",
              }}
            >
              <Bot size={12} /> DRR · AI Assistant
            </span>
            <h1
              style={{
                marginTop: "14px",
                marginBottom: "8px",
                fontSize: "clamp(1.75rem, 5vw, 2.75rem)",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              DRR AI Assistant
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.70)",
                fontSize: "15px",
                lineHeight: 1.55,
                maxWidth: "560px",
                margin: 0,
              }}
            >
              Ask anything about water, fire, mold, biohazard, ICRA, roofing,
              or any restoration topic. I&apos;ll ask the right questions and give
              you specific answers with the math shown.
            </p>
          </div>

          {/* ── Disclaimer ── */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              background: "rgba(212,168,75,0.07)",
              border: "1px solid rgba(212,168,75,0.22)",
              borderRadius: "10px",
              padding: "11px 14px",
              marginBottom: "18px",
            }}
          >
            <AlertTriangle
              size={15}
              style={{ color: "#D4A84B", flexShrink: 0, marginTop: "2px" }}
            />
            <p
              style={{
                color: "rgba(255,255,255,0.68)",
                fontSize: "13px",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              <strong style={{ color: "#D4A84B" }}>Not HIPAA compliant.</strong>{" "}
              Do not share Patient Health Information (PHI) or sensitive facility
              data.
            </p>
          </div>

          {/* ── Chat card ── */}
          <div
            style={{
              background: "rgba(7,15,28,0.92)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.09)",
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: "0 8px 48px rgba(0,0,0,0.6)",
            }}
          >
            {/* Messages */}
            <div
              ref={scrollRef}
              style={{
                height: "520px",
                overflowY: "auto",
                padding: "20px 18px",
                display: "flex",
                flexDirection: "column",
                gap: "18px",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.10) transparent",
              }}
            >
              {messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent:
                      m.role === "user" ? "flex-end" : "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  {m.role === "assistant" && (
                    <div
                      style={{
                        flexShrink: 0,
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        border: "1px solid rgba(220,38,38,0.45)",
                        background: "rgba(220,38,38,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#D4A84B",
                        marginTop: "2px",
                      }}
                    >
                      <Bot size={14} />
                    </div>
                  )}

                  <div
                    style={{
                      maxWidth: "80%",
                      borderRadius:
                        m.role === "user"
                          ? "16px 16px 4px 16px"
                          : "16px 16px 16px 4px",
                      padding: m.role === "user" ? "10px 14px" : "14px 16px",
                      background:
                        m.role === "user"
                          ? "#DC2626"
                          : "rgba(13,24,42,0.98)",
                      border:
                        m.role === "assistant"
                          ? "1px solid rgba(255,255,255,0.07)"
                          : "none",
                    }}
                  >
                    {m.role === "assistant" ? (
                      <AssistantMarkdown content={m.content} />
                    ) : (
                      <span
                        style={{
                          color: "#ffffff",
                          fontSize: "14px",
                          lineHeight: "1.6",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {m.content}
                      </span>
                    )}
                  </div>

                  {m.role === "user" && (
                    <div
                      style={{
                        flexShrink: 0,
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.10)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.55)",
                        marginTop: "2px",
                      }}
                    >
                      <User size={14} />
                    </div>
                  )}
                </div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      border: "1px solid rgba(220,38,38,0.45)",
                      background: "rgba(220,38,38,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#D4A84B",
                    }}
                  >
                    <Bot size={14} />
                  </div>
                  <div
                    style={{
                      borderRadius: "16px 16px 16px 4px",
                      padding: "13px 16px",
                      background: "rgba(13,24,42,0.98)",
                      border: "1px solid rgba(255,255,255,0.07)",
                      display: "flex",
                      gap: "5px",
                      alignItems: "center",
                    }}
                  >
                    <span className="size-2 animate-bounce rounded-full bg-zinc-500 [animation-delay:-0.3s]" />
                    <span className="size-2 animate-bounce rounded-full bg-zinc-500 [animation-delay:-0.15s]" />
                    <span className="size-2 animate-bounce rounded-full bg-zinc-500" />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  style={{
                    borderRadius: "10px",
                    padding: "11px 14px",
                    background: "rgba(220,38,38,0.08)",
                    border: "1px solid rgba(220,38,38,0.28)",
                    color: "#fca5a5",
                    fontSize: "13px",
                    lineHeight: "1.5",
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Input bar */}
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.07)",
                padding: "14px 16px",
                background: "rgba(4,10,20,0.75)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-end",
                }}
              >
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInput}
                  onKeyDown={onKey}
                  placeholder="Describe your situation — e.g. pipe burst in bedroom and hallway, discovered 3 hours ago..."
                  disabled={loading}
                  autoFocus
                  rows={1}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "10px",
                    padding: "11px 13px",
                    color: "#ffffff",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    resize: "none",
                    outline: "none",
                    minHeight: "44px",
                    maxHeight: "130px",
                    overflowY: "auto",
                    fontFamily: "inherit",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(220,38,38,0.45)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.12)";
                  }}
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={loading || !input.trim()}
                  style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background:
                      input.trim() && !loading
                        ? "#DC2626"
                        : "rgba(220,38,38,0.28)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0 18px",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor:
                      input.trim() && !loading ? "pointer" : "not-allowed",
                    transition: "background 0.15s",
                    height: "44px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Send size={14} />
                  Send
                </button>
              </div>
              <p
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.30)",
                  lineHeight: "1.4",
                }}
              >
                Enter to send · Shift+Enter for new line · Trained on IICRC
                S500/S520/S700/S540, ICRA 2.0, NESHAP, AZDEQ
              </p>
            </div>
          </div>

          {/* ── Emergency CTA ── */}
          <div
            style={{
              marginTop: "18px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "14px",
              background: "rgba(220,38,38,0.09)",
              border: "1px solid rgba(220,38,38,0.26)",
              borderRadius: "14px",
              padding: "18px 22px",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  color: "#D4A84B",
                }}
              >
                Active emergency?
              </p>
              <p
                style={{
                  margin: "3px 0 0",
                  fontSize: "16px",
                  fontWeight: 800,
                  color: "#ffffff",
                }}
              >
                Skip the chat — dispatch is standing by 24/7.
              </p>
            </div>
            <a
              href={BRAND.phoneHref}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "#DC2626",
                color: "#fff",
                fontWeight: 700,
                fontSize: "15px",
                borderRadius: "100px",
                padding: "11px 24px",
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              <Phone size={14} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

